'use client';

import { ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Printer } from '@phosphor-icons/react';
import { VisualizationWrapper } from './visualization-wrapper';
import { useVisualizationContext } from './context';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { captureProductTelemetry } from '@/lib/product-telemetry';

/**
 * Open the visualization HTML in a new window and trigger the browser's native print dialog.
 * Uses the browser's PDF engine — no libraries, preserves text selection, custom fonts, and all CSS.
 */
function openPrintWindow({ title, html, css }: { title?: string; html: string; css?: string }) {
  const win = window.open('', '_blank', 'width=900,height=700');
  if (!win) {
    console.warn('Print window was blocked — check popup settings.');
    return;
  }
  const doc = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>${title ? title.replace(/[<>]/g, '') : 'Print'}</title>
<style>
  *, *::before, *::after { box-sizing: border-box; }
  html, body { margin: 0; padding: 24px; font-family: system-ui, -apple-system, sans-serif; color: #111827; }
  img, svg, canvas, video { max-width: 100%; }
  @media print {
    body { padding: 0; }
  }
  ${css || ''}
</style>
</head>
<body>${html}</body>
</html>`;
  win.document.open();
  win.document.write(doc);
  win.document.close();
  // Wait for images/fonts to load before printing
  win.addEventListener('load', () => {
    // Small delay helps with webfonts/images in some browsers
    setTimeout(() => {
      win.focus();
      win.print();
    }, 150);
  });
}

function PrintHtmlButton({ title, html, css }: { title?: string; html: string; css?: string }) {
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => openPrintWindow({ title, html, css })}
      className="shrink-0 h-7 sm:h-8 px-1.5 sm:px-2"
      title="Print or save as PDF"
    >
      <Printer weight="bold" className="size-3.5 sm:size-4" />
      <span className="ml-1 text-xs hidden md:inline">Print</span>
    </Button>
  );
}

interface BaseMarkupProps {
  title?: string;
  markup: string;
  css?: string;
  minHeight?: number;
  maxHeight?: number;
  showTitle?: boolean;
  fit?: 'auto' | 'contain';
  visualizationId?: string;
  renderActions?: ReactNode;
  className?: string;
  displayContext?: 'inline' | 'artifact';
}

interface MarkupRendererProps extends BaseMarkupProps {
  kind: 'html' | 'svg';
}

function buildDocument({
  kind,
  markup,
  css,
  resizeMessageType,
  runtimeErrorMessageType,
}: {
  kind: 'html' | 'svg';
  markup: string;
  css?: string;
  resizeMessageType: string;
  runtimeErrorMessageType: string;
}) {
  const isSvg = kind === 'svg';
  const isHtml = kind === 'html';
  const bodyMarkup = kind === 'svg'
    ? `<div class="svg-shell">${markup}</div>`
    : markup;

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    *, *::before, *::after {
      box-sizing: border-box;
    }

    html, body {
      margin: 0;
      padding: 0;
      width: 100%;
      background: transparent;
      color: #111827;
      font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    }

    body {
      overflow-x: hidden;
      ${isHtml ? 'overflow-y: auto;' : ''}
      ${isHtml ? 'min-height: 0 !important;' : ''}
      ${isHtml ? 'height: auto !important;' : ''}
    }

    ${isHtml ? 'html { min-height: 0 !important; height: auto !important; }' : ''}

    img, svg, canvas, iframe, video {
      max-width: 100%;
    }

    .svg-shell {
      width: 100%;
      padding: 0;
    }

    .svg-shell svg {
      display: block;
      width: 100%;
      height: auto;
      max-width: 100%;
    }

    #markup-root {
      width: 100%;
    }

    ${css || ''}
  </style>
  <script>
    const messageType = ${JSON.stringify(resizeMessageType)};
    const runtimeErrorType = ${JSON.stringify(runtimeErrorMessageType)};
    const isSvg = ${JSON.stringify(isSvg)};
    let lastHeight = 0;
    let scheduled = false;
    let lastRuntimeError = '';

    function getHeight() {
      if (isSvg) {
        const svg = document.querySelector('.svg-shell svg');
        if (!svg) return 0;

        const rect = svg.getBoundingClientRect();
        return Math.ceil(rect.height || 0);
      }

      const root = document.getElementById('markup-root');
      if (!root) return 0;

      const rect = root.getBoundingClientRect();
      return Math.max(
        root.scrollHeight || 0,
        root.offsetHeight || 0,
        rect.height || 0
      );
    }

    function sendHeight() {
      scheduled = false;
      const height = Math.ceil(getHeight());
      if (height > 0 && height < 20000 && Math.abs(height - lastHeight) > 1) {
        lastHeight = height;
        window.parent.postMessage({ type: messageType, height }, '*');
      }
    }

    function scheduleHeight() {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(sendHeight);
    }

    function postRuntimeError(message) {
      const normalized = String(message || 'Unknown runtime error').slice(0, 500);
      if (!normalized || normalized === lastRuntimeError) return;
      lastRuntimeError = normalized;
      window.parent.postMessage({ type: runtimeErrorType, error: normalized }, '*');
    }

    window.addEventListener('error', (event) => {
      postRuntimeError(event.message || 'Runtime error');
    });

    window.addEventListener('unhandledrejection', (event) => {
      const reason = event.reason;
      postRuntimeError(reason && typeof reason === 'object' && 'message' in reason ? reason.message : reason);
    });

    window.addEventListener('load', scheduleHeight);
    window.addEventListener('resize', scheduleHeight);
    document.addEventListener('DOMContentLoaded', scheduleHeight);

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(scheduleHeight);
      const target = isSvg
        ? document.querySelector('.svg-shell svg')
        : document.getElementById('markup-root');
      if (target) resizeObserver.observe(target);
    }

    if (!isSvg && 'MutationObserver' in window && document.body) {
      const mutationObserver = new MutationObserver(scheduleHeight);
      mutationObserver.observe(document.getElementById('markup-root') || document.body, {
        attributes: true,
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    Array.from(document.images || []).forEach((img) => {
      if (!img.complete) {
        img.addEventListener('load', scheduleHeight);
        img.addEventListener('error', scheduleHeight);
      }
    });

    setTimeout(scheduleHeight, 0);
    setTimeout(scheduleHeight, 100);
    setTimeout(scheduleHeight, 500);
    setTimeout(scheduleHeight, 1500);
  </script>
</head>
<body>
  <div id="markup-root">${bodyMarkup}</div>
</body>
</html>
  `.trim();
}

export function MarkupRenderer({
  kind,
  title,
  markup,
  css,
  minHeight = kind === 'svg' ? 260 : 320,
  maxHeight,
  showTitle = true,
  fit,
  visualizationId,
  renderActions,
  className,
  displayContext = 'inline',
}: MarkupRendererProps) {
  const messageId = useId();
  const resizeMessageType = `markupRendererResize:${messageId}`;
  const runtimeErrorMessageType = `markupRendererRuntimeError:${messageId}`;
  const { isFullscreen, viewMode } = useVisualizationContext();
  const iframeWrapperRef = useRef<HTMLDivElement>(null);
  const [fitScale, setFitScale] = useState(1);
  const [inlineHeight, setInlineHeight] = useState<number>(minHeight);
  const [fullscreenHeight, setFullscreenHeight] = useState<number>(minHeight);
  // Uncapped natural content height (raw from iframe postMessage). Needed for fit-to-screen so the whole tall content scales down, not just the capped 900px slice.
  const [naturalHeight, setNaturalHeight] = useState<number>(minHeight);
  const [viewportHeight, setViewportHeight] = useState<number>(
    typeof window !== 'undefined' ? window.innerHeight : 1000
  );
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const lastRuntimeErrorRef = useRef<string | null>(null);
  const renderStartRef = useRef(typeof performance !== 'undefined' ? performance.now() : Date.now());
  const hasTrackedReadyRef = useRef(false);

  const fullDoc = useMemo(
    () => buildDocument({ kind, markup, css, resizeMessageType, runtimeErrorMessageType }),
    [kind, markup, css, resizeMessageType, runtimeErrorMessageType],
  );

  useEffect(() => {
    renderStartRef.current = typeof performance !== 'undefined' ? performance.now() : Date.now();
    hasTrackedReadyRef.current = false;
    lastRuntimeErrorRef.current = null;
  }, [displayContext, fullDoc, isFullscreen]);

  useEffect(() => {
    setInlineHeight(prev => Math.max(minHeight, prev));
    setFullscreenHeight(prev => Math.max(minHeight, prev));
  }, [minHeight]);

  useEffect(() => {
    setInlineHeight(minHeight);
    setFullscreenHeight(minHeight);
  }, [isFullscreen, minHeight]);

  useEffect(() => {
    const updateViewportHeight = () => setViewportHeight(window.innerHeight);
    window.addEventListener('resize', updateViewportHeight);
    return () => window.removeEventListener('resize', updateViewportHeight);
  }, []);

  const effectiveMaxHeight = useMemo(() => {
    if (maxHeight) return maxHeight;
    if (kind !== 'html') return undefined;
    return isFullscreen
      ? Math.max(minHeight, viewportHeight - 120)
      : Math.max(minHeight, 900);
  }, [isFullscreen, kind, maxHeight, minHeight, viewportHeight]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) {
        return;
      }

      if (event.data?.type === runtimeErrorMessageType && typeof event.data.error === 'string') {
        const errorKey = `${kind}:${visualizationId ?? title ?? 'markup'}:${event.data.error}`;
        if (lastRuntimeErrorRef.current !== errorKey) {
          lastRuntimeErrorRef.current = errorKey;
          captureProductTelemetry('visualization_render_failed', {
            tool_name: kind,
            failure_kind: 'iframe-runtime-error',
            visualization_id: visualizationId,
            display_context: displayContext,
          });
        }
        return;
      }

      if (event.data?.type !== resizeMessageType || typeof event.data.height !== 'number') {
        return;
      }

      const paddedHeight = event.data.height + 16;
      const rawNatural = Math.max(minHeight, paddedHeight);
      const boundedHeight = effectiveMaxHeight
        ? Math.min(effectiveMaxHeight, rawNatural)
        : rawNatural;

      if (!hasTrackedReadyRef.current) {
        hasTrackedReadyRef.current = true;
        captureProductTelemetry('visualization_render_ready', {
          tool_name: kind,
          visualization_id: visualizationId,
          display_context: displayContext,
          render_duration_ms: Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - renderStartRef.current),
        });
      }

      setNaturalHeight(prev => (Math.abs(prev - rawNatural) > 1 ? rawNatural : prev));

      if (isFullscreen) {
        setFullscreenHeight(prev => (Math.abs(prev - boundedHeight) > 1 ? boundedHeight : prev));
      } else {
        setInlineHeight(prev => (Math.abs(prev - boundedHeight) > 1 ? boundedHeight : prev));
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [displayContext, effectiveMaxHeight, isFullscreen, kind, minHeight, resizeMessageType, runtimeErrorMessageType, title, visualizationId]);

  const contentHeight = isFullscreen ? fullscreenHeight : inlineHeight;
  // In fit mode, render the iframe at its FULL natural height (not the 900px cap) so scaling shows the entire content.
  const frameHeight = viewMode === 'fit'
    ? naturalHeight
    : kind === 'html' && isFullscreen && effectiveMaxHeight
      ? effectiveMaxHeight
      : contentHeight;

  // When viewMode is "fit", scale the iframe so the visualization fills the
  // available height. In fullscreen we ALSO scale UP (cap at 2.5×) so a small
  // viz doesn't sit tiny in a sea of dead canvas — the user's complaint. In
  // inline contexts we keep the ≤1 cap to avoid awkward zoom-ups.
  useEffect(() => {
    if (viewMode !== 'fit') {
      setFitScale(1);
      return;
    }
    const compute = () => {
      const wrapper = iframeWrapperRef.current;
      if (!wrapper) return;
      const availableH = wrapper.clientHeight;
      if (availableH > 0 && naturalHeight > 0) {
        const ratio = availableH / naturalHeight;
        const upperCap = isFullscreen ? 2.5 : 1;
        setFitScale(Math.min(ratio, upperCap));
      }
    };
    compute();
    const ro = new ResizeObserver(compute);
    if (iframeWrapperRef.current) ro.observe(iframeWrapperRef.current);
    return () => ro.disconnect();
  }, [viewMode, naturalHeight, isFullscreen]);

  const exportPayload = kind === 'html'
    ? { title, html: markup, css, minHeight, maxHeight }
    : { title, svg: markup, css, minHeight, maxHeight };

  // For HTML, prepend a Print button (native browser print → Save as PDF is the highest-fidelity export).
  const combinedRenderActions = kind === 'html' ? (
    <>
      <PrintHtmlButton title={title} html={markup} css={css} />
      {renderActions}
    </>
  ) : renderActions;

  return (
    <VisualizationWrapper
      title={showTitle ? (title || (kind === 'html' ? 'HTML Visualization' : 'SVG Visualization')) : undefined}
      minHeight={contentHeight + 56}
      flexHeight
      headerMode="outside"
      visualizationId={visualizationId}
      exportData={{ type: kind, content: exportPayload }}
      renderActions={combinedRenderActions}
      allowFitToggle
      initialViewMode={fit === 'contain' ? 'fit' : 'scroll'}
      className={className}
    >
      <div
        ref={iframeWrapperRef}
        className={cn(
          'w-full',
          isFullscreen ? 'h-full p-0' : 'px-2 pb-2',
          viewMode === 'fit' && 'h-full overflow-hidden flex justify-center',
          // Center vertically too in fullscreen-fit so any leftover space
          // after scaling sits balanced top + bottom, not all below.
          viewMode === 'fit' && isFullscreen && 'items-center',
        )}
      >
        <iframe
          ref={iframeRef}
          key={`${kind}:${isFullscreen ? 'fullscreen' : 'inline'}`}
          srcDoc={fullDoc}
          title={title || (kind === 'html' ? 'HTML Visualization' : 'SVG Visualization')}
          sandbox="allow-scripts"
          className={cn(
            'w-full border-0 bg-transparent',
            isFullscreen ? 'h-full rounded-none' : 'rounded-md'
          )}
          style={{
            height: `${frameHeight}px`,
            ...(viewMode === 'fit' ? {
              transform: `scale(${fitScale})`,
              transformOrigin: 'top center',
            } : undefined),
          }}
        />
      </div>
    </VisualizationWrapper>
  );
}

export function HtmlMarkupRenderer(props: BaseMarkupProps) {
  return <MarkupRenderer kind="html" {...props} />;
}

export function SvgMarkupRenderer(props: BaseMarkupProps) {
  return <MarkupRenderer kind="svg" {...props} />;
}
